import { useState } from 'react';
import { AnimateHeight, Button, LinkBase, Text } from '@belong/ui';
import { type School } from 'src/features/homeowners-neighborhood/types';
import { SCHOOL_LEVEL_MAP } from '../../constants';

type NearbySchoolsSectionProps = {
  schools: School[];
};

export function NearbySchoolsSection({ schools }: NearbySchoolsSectionProps) {
  const [isOpen, setIsOpen] = useState(false);

  const schoolsOrderedByRanking = [...schools].sort((schoolA, schoolB) =>
    Number(schoolA.rating) < Number(schoolB.rating) ? 1 : -1,
  );

  return (
    <>
      <div className="mb-sm gap-y-sm lg:gap-y-xl grid grid-cols-1 lg:grid-cols-2">
        {schoolsOrderedByRanking.slice(0, 4).map((school) => {
          return (
            <NearbySchool
              key={school.universalId}
              url={school.overviewUrl}
              rating={school.rating}
              name={school.name}
              level={school.levelCodes}
            />
          );
        })}
      </div>
      <AnimateHeight
        id="school-panel"
        duration={400}
        height={isOpen ? 'auto' : 0}
      >
        <div className="mb-sm gap-y-sm lg:gap-y-xl lg:pt-sm grid grid-cols-1 pt-0 lg:grid-cols-2">
          {schoolsOrderedByRanking.slice(4).map((school) => {
            return (
              <NearbySchool
                key={school.universalId}
                url={school.overviewUrl}
                rating={school.rating}
                name={school.name}
                level={school.levelCodes}
              />
            );
          })}
        </div>
      </AnimateHeight>
      {schoolsOrderedByRanking.length >= 5 && (
        <div className="flex justify-end">
          <Button
            variant="text"
            aria-expanded={isOpen}
            aria-controls="school-panel"
            onClick={() => setIsOpen((prev) => !prev)}
          >
            {isOpen ? 'Show Less' : 'Show More'}
          </Button>
        </div>
      )}
    </>
  );
}

function NearbySchool({
  rating,
  name,
  level,
  url,
}: {
  rating: number;
  name: string;
  level: string;
  url: string;
}) {
  return (
    <LinkBase href={url} target="_blank" className="flex items-center">
      <>
        <span>
          <Text as="span" variant="h2" fontWeight="semibold">
            {rating}
          </Text>
          <Text as="span" fontWeight="semibold">
            /10
          </Text>
        </span>
        <div className="ml-sm">
          <Text>{name}</Text>
          <Text variant="p1" className="text-dark-gray">
            {level
              .split(',')
              .map((level) => SCHOOL_LEVEL_MAP[level])
              .join(', ')}
          </Text>
        </div>
      </>
    </LinkBase>
  );
}
